import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useApolloClient } from '@apollo/client';
import { ME_QUERY } from '../graphql/queries';
import { hasAdminModule, isStaff, isSuperAdmin } from '../utils/staff';

interface UserPermissions {
  sceneVisible: boolean;
  matterportVisible: boolean;
  releaseNotesVisible: boolean;
  gaussianVisible: boolean;
  gaussianGenerate: boolean;
  orthomosaicVisible: boolean;
  video360Visible: boolean;
  basemapVisible: boolean;
  lgsxUpload: boolean;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  industry?: string;
  role: string;
  /** The admin pages an 'admin' may use; super admins have all. */
  adminModules?: string[];
  /** A subuser uses its main user's plan, limits and feature switches (account). */
  isSubuser?: boolean;
  account?: { id: string; firstName: string; lastName: string; email: string; companyName: string } | null;
  avatar?: string;
  sendEmailsTo?: string | null;
  permissions: UserPermissions;
  planName?: string;
  accountStorageLimit?: number;
  accountStorageUsed?: number;
  downloadsExceeded?: boolean;
  trialEndsAt?: string | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  /** Any admin (super admin or admin with modules): the Admin area is open to them. */
  isStaff: boolean;
  isSuperAdmin: boolean;
  hasModule: (module: string) => boolean;
  loading: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const client = useApolloClient();

  useEffect(() => {
    const storedToken = localStorage.getItem('geodraft_token');
    if (!storedToken) {
      setLoading(false);
      return;
    }
    setToken(storedToken);
    client.query({ query: ME_QUERY, fetchPolicy: 'network-only' })
      .then(({ data }) => { if (data?.me) setUser(data.me); })
      .catch(() => {
        localStorage.removeItem('geodraft_token');
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = (newToken: string, newUser: User) => {
    localStorage.setItem('geodraft_token', newToken);
    setToken(newToken);
    setUser(newUser);
    client.query({ query: ME_QUERY, fetchPolicy: 'network-only' })
      .then(({ data }) => { if (data?.me) setUser(data.me); })
      .catch(() => {});
  };

  const logout = () => {
    localStorage.removeItem('geodraft_token');
    setToken(null);
    setUser(null);
    client.clearStore();
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider value={{
      user, token, isAuthenticated: !!token, loading, login, logout, updateUser,
      isStaff: isStaff(user), isSuperAdmin: isSuperAdmin(user), hasModule: (m) => hasAdminModule(user, m),
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
